import {
  BaseInputProps,
  Dropdown,
  Option,
  useFormContextRequired
} from 'ui-builder';
import {
  Plan,
  ProjectType,
  useDetailsFieldsSetsService,
  usePlansService
} from 'nekst-api';
import React, { useMemo, useState } from 'react';
import { get } from 'lodash';
import useDataLoader, { useDataLoaderNew } from 'data-loader';

export interface ProjectPlanOfTypeDropdownProps extends BaseInputProps<number> {
  projectTypeSource: string;
  withContractTemplateAssigned?: boolean;
  withKeyDatesOnly?: boolean;
  setupAreaIdSource?: string;
}

function useSetsLoader(projectType?: ProjectType) {
  const setService = useDetailsFieldsSetsService();

  const { data: allSets } = useDataLoaderNew(
    setService.getAllSets
  );

  const sets = useMemo(() => {
    if (projectType) {
      return (allSets || []).filter((set) => {
        return set.projectType === projectType;
      });
    } else {
      return [];
    }

  }, [allSets, projectType]);

  return {
    sets
  };
}

export function ProjectPlanOfTypeDropdown(props: ProjectPlanOfTypeDropdownProps) {
  const planService = usePlansService();

  const [plans, setPlans] = useState<Plan[]>();

  const formContext = useFormContextRequired();

  const projectType = get(formContext.data, props.projectTypeSource);

  const setupAreaId = props.setupAreaIdSource
    ? get(formContext.data, props.setupAreaIdSource)
    : undefined;

  const { sets } = useSetsLoader(projectType);

  const { loading } = useDataLoader(
    async () => {
      if (projectType) {
        const allPlans = await planService.getAllTeamPlans(projectType, setupAreaId);

        return allPlans
          .filter((item) => {
            if (props.withContractTemplateAssigned) {
              return item.isLaunchingFromContractSupported;
            } else {
              return true;
            }
          });
      } else {
        return [];
      }
    },
    setPlans,
    projectType,
    setupAreaId
  );

  let placeholder;

  if (!loading) {
    if (plans?.length) {
      placeholder = 'Choose Workflow';
    } else if (projectType) {
      placeholder = 'No workflows available for chosen Transaction Type';
    } else {
      placeholder = 'Please select a Transaction Type first';
    }
  } else {
    placeholder = 'Loading...';
  }

  const getLabel = (item: Plan) => {
    let name = item.name;

    if (item._detailsFieldsSet) {
      name += ` (${item._detailsFieldsSet.name})`;
    }

    return name;
  };

  const hasSets = formContext.data?._hasSets;

  const options = useMemo((): Option<string | number>[] => {
    if (plans) {
      return [
        ...plans.map((item) => ({
          value: item.id,
          label: getLabel(item)
        })),
        ...props.withKeyDatesOnly && hasSets ? sets.map((item) => ({
          value: `set:${item.id}`,
          label: `Key Dates Only: ${item.name}`
        })) : []
      ];
    } else {
      return [];
    }
  }, [JSON.stringify(sets), JSON.stringify(plans)]);

  return (
    <Dropdown<string | number>
      {...props}
      options={options}
      placeholder={placeholder}
    />
  );
}
